import React, { FC, useRef, useState } from 'react';
import { components, MultiValue } from 'react-select';
import { BrandLogo, Checkbox, Chip, Icon, Select } from '@kontentino/ui';
import { usePostComposer } from 'app/modules/posts/modules/post/components/composer/PostComposerProvider';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { queryKey } from 'constants/queryKey';
import { PageData } from 'types/Page';
import getSocialMediaTypeForBrandLogo from 'app/utils/getSocialMediaTypeForBrandLogo';
import useQueryNoCache from 'utils/hooks/useQueryNoCache';
import { PostDetail } from 'types/PostDetail';
import PostApi from 'api/post';
import clsx from 'clsx';
import PageApi from 'api/page';
import { useOutsideClick } from 'utils/hooks/useOutsideClick';
import { getYtLogoGuidelineSize } from 'app/utils/getytlogoguidelinesize';
import TranslationLanguageDropdown, {
  TranslationSettings,
} from 'app/modules/posts/modules/translation/TranslationLanguageDropdown';
import { useUserPermissions } from 'app/hooks/useUserPermissions';
import { UserPermission } from 'constants/userPermission';
import { faXmark } from '@fortawesome/pro-regular-svg-icons/faXmark';

const ALL_OPTION = {
  label: 'Select all',
  value: -1,
  page: undefined,
};

const DEFAULT_TRANSLATION_SETTINGS: TranslationSettings = {
  translate: null,
};

type LocalPageOption = {
  label: string;
  value: number;
  page?: PageData;
};

const PostComposerContentLocalPostsProfileSelect: FC = () => {
  const [isMenuOpen, setMenuIsOpen] = useState(false);
  const isAllSelected = useRef(false);
  const { elementRef, addListener, removeListener } =
    useOutsideClick<HTMLDivElement>(handleCloseMenu);
  const { form, setForm, translationSettings, setTranslationSettings } =
    usePostComposer();
  const { t } = useTranslation();
  const { hasSome } = useUserPermissions();
  const canTranslate = hasSome(UserPermission.POST_EDIT_TEXT);
  const [brandProfilesByPage, setBrandProfilesByPage] = useState<
    Record<number, number | null>
  >({});
  const [sessionSelectedPages, setSessionSelectedPages] = useState<PageData[]>(
    [],
  );

  function handleToggleMenuOpen() {
    if (isMenuOpen) {
      handleCloseMenu();
    } else {
      handleOpenMenu();
    }
  }

  function handleOpenMenu() {
    setMenuIsOpen(true);
    addListener();
  }

  function handleCloseMenu() {
    setMenuIsOpen(false);
    removeListener();
  }

  const { data: localPages, isLoading: isLocalPagesLoading } = useQuery(
    queryKey.localPages(form.page.id),
    () => PageApi.localPages(form.page.id),
  );

  const { data: postData, isLoading: loadingPostDetailPages } =
    useQueryNoCache<PostDetail>(
      queryKey.postDetail(form.id!),
      () => PostApi.detail(form.id!),
      {
        enabled: !!form.id,
      },
    );

  const selectedLocalProfilesInPost =
    postData?.localPosts?.data.map((post) => post.page.id) || [];

  const filteredPages: PageData[] = (localPages?.data ?? []).filter(
    (page) => !selectedLocalProfilesInPost.includes(page.id),
  );

  const pagesToOptions = (pages: PageData[]): LocalPageOption[] =>
    pages.map((page) => ({
      label: page.name,
      value: page.id,
      page,
    }));

  const options =
    filteredPages.length > 0
      ? [ALL_OPTION, ...pagesToOptions(filteredPages)]
      : pagesToOptions(filteredPages);

  const handleToggleAllNone = (isSelectingAll: boolean) => {
    if (isSelectingAll) {
      setSessionSelectedPages(filteredPages);
      setForm({ distributeToPageIds: filteredPages.map((page) => page.id) });
    } else {
      setSessionSelectedPages([]);
      setForm({ distributeToPageIds: [] });
      setTranslationSettings({});
    }
    isAllSelected.current = isSelectingAll;
  };

  const handleChange = (selectedOptions: LocalPageOption[]) => {
    const isAllNoneSelected = selectedOptions.some(
      (option) => option.value === ALL_OPTION.value,
    );

    if (isAllNoneSelected) {
      handleToggleAllNone(!isAllSelected.current);
    } else {
      const newPages = selectedOptions
        .filter((option) => option.page)
        .map((option) => option.page!);

      setSessionSelectedPages(newPages);
      setForm({ distributeToPageIds: newPages.map((page) => page.id) });
      isAllSelected.current = newPages.length === filteredPages.length;

      const newPageIds = new Set(newPages.map((p) => p.id));
      setTranslationSettings((prev) => {
        const updated: Record<number, TranslationSettings> = {};
        for (const [pageId, settings] of Object.entries(prev)) {
          if (newPageIds.has(Number(pageId))) {
            updated[Number(pageId)] = settings;
          }
        }
        return updated;
      });
    }
  };

  const handleRemoveProfile = (pageId: number) => {
    const newPages = sessionSelectedPages.filter((page) => page.id !== pageId);
    setSessionSelectedPages(newPages);
    setForm({ distributeToPageIds: newPages.map((page) => page.id) });
    isAllSelected.current = newPages.length === filteredPages.length;

    setTranslationSettings((prev) => {
      const updated = { ...prev };
      delete updated[pageId];
      return updated;
    });
  };

  const handleTranslationChange = (
    pageId: number,
    settings: TranslationSettings,
  ) => {
    setTranslationSettings((prev) => ({
      ...prev,
      [pageId]: settings,
    }));
  };

  const getTranslationSettingsForPage = (pageId: number): TranslationSettings =>
    translationSettings[pageId] || DEFAULT_TRANSLATION_SETTINGS;

  const customComponents = {
    ValueContainer: (containerProps: any) => {
      const selectedCount = sessionSelectedPages.length;

      return (
        <components.ValueContainer {...containerProps}>
          <div
            className="tw-flex tw-w-full tw-items-center tw-gap-x-2  hover:tw-cursor-pointer"
            onClick={handleToggleMenuOpen}
            data-cy="post-composer_local-posts_profile-select"
          >
            <Chip data-cy="local-profiles_selected-count">{selectedCount}</Chip>
            <span>{t('profileSelectedLower', { count: selectedCount })}</span>
          </div>
        </components.ValueContainer>
      );
    },
    Option: (optionProps: any) => {
      const isSelected = isAllSelected.current || optionProps.isSelected;
      const isAllNoneOption = optionProps.data.value === ALL_OPTION.value;

      return (
        <components.Option
          {...optionProps}
          className={clsx(optionProps.className, {
            '!tw-bg-primary-10': isSelected,
          })}
        >
          <div
            className="tw-flex tw-items-center tw-justify-between tw-gap-x-2"
            data-cy="local-profile_option"
          >
            <div className="tw-flex tw-items-center tw-gap-x-2">
              <Checkbox
                checked={isSelected}
                size="small"
                onChange={() => null}
                data-cy={`local-profile_option-checkbox`}
              />

              {optionProps.data.page && (
                <BrandLogo
                  src={optionProps.data.page.logo?.src}
                  name={optionProps.data.label}
                  size={28}
                  socialMediaType={getSocialMediaTypeForBrandLogo(
                    optionProps.data.page.type,
                  )}
                  iconSize={getYtLogoGuidelineSize(
                    12,
                    optionProps.data.page.type,
                  )}
                />
              )}
              <div className="tw-truncate">{optionProps.data.label}</div>
            </div>

            {!isAllNoneOption &&
              optionProps.data.page &&
              isSelected &&
              canTranslate && (
                <div
                  onClick={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <TranslationLanguageDropdown
                    pageId={optionProps.data.page.id}
                    value={getTranslationSettingsForPage(
                      optionProps.data.page.id,
                    )}
                    onChange={(settings) =>
                      handleTranslationChange(
                        optionProps.data.page.id,
                        settings,
                      )
                    }
                    selectedBrandProfileId={
                      brandProfilesByPage[optionProps.data.page.id] ?? null
                    }
                    onBrandProfileChange={(brandProfileId) =>
                      setBrandProfilesByPage((prev) => ({
                        ...prev,
                        [optionProps.data.page.id]: brandProfileId,
                      }))
                    }
                  />
                </div>
              )}
          </div>
        </components.Option>
      );
    },
  };

  return (
    <div ref={elementRef}>
      <Select
        isMulti
        menuPlacement="auto"
        isLoading={isLocalPagesLoading}
        isCreatable={false}
        isDisabled={loadingPostDetailPages}
        options={options}
        getOptionValue={(option: LocalPageOption) => String(option.value)}
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        menuIsOpen={isMenuOpen}
        onMenuOpen={handleOpenMenu}
        onMenuClose={handleCloseMenu}
        components={customComponents}
        onBlur={handleCloseMenu}
        onChange={(selected: MultiValue<LocalPageOption>) =>
          handleChange(selected as LocalPageOption[])
        }
        value={sessionSelectedPages.map((page) => ({
          label: page.name,
          value: page.id,
          page,
        }))}
        noOptionsMessage={() => t('globalPages.noAvailableLocalPages')}
      />

      {sessionSelectedPages.length > 0 && (
        <div className="tw-mt-3 tw-flex tw-flex-col tw-gap-2">
          {sessionSelectedPages.map((page) => {
            return (
              <div
                key={page.id}
                className="tw-flex tw-items-center tw-justify-between tw-rounded-lg tw-border tw-border-grayscale-20 tw-px-3 tw-py-2"
              >
                <div className="tw-flex tw-items-center tw-gap-2">
                  <BrandLogo
                    src={page.logo?.src}
                    name={page.name}
                    size={24}
                    socialMediaType={getSocialMediaTypeForBrandLogo(page.type)}
                    iconSize={getYtLogoGuidelineSize(10, page.type)}
                  />
                  <span className="tw-text-sm tw-text-grayscale-180">
                    {page.name}
                  </span>
                </div>
                <div className="tw-flex tw-items-center tw-gap-2">
                  {canTranslate && (
                    <TranslationLanguageDropdown
                      pageId={page.id}
                      value={getTranslationSettingsForPage(page.id)}
                      onChange={(settings) =>
                        handleTranslationChange(page.id, settings)
                      }
                    />
                  )}
                  <button
                    type="button"
                    onClick={() => handleRemoveProfile(page.id)}
                    className="tw-text-grayscale-100 hover:tw-text-danger-100"
                  >
                    <Icon icon={faXmark} size="sm" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PostComposerContentLocalPostsProfileSelect;
